/**
 * The signed-in user and their workspace (GET /api/auth/me), shared by the whole app.
 * Inside the authenticated layout the session is always loaded, so useSession() never
 * returns undefined there.
 */
import { queryOptions, useQuery, type QueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "./client";
import type { DecimalString, Uuid } from "./types";

export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_staff: boolean;
  is_superuser: boolean;
}

export interface ShipTo {
  line1: string;
  city: string;
  region: string;
  postal_code: string;
  /** ISO 3166-1 alpha-2, e.g. "CA". */
  country: string;
}

export type WorkspaceRole = "owner" | "editor" | "viewer";

/** Offsets for one label template, applied when rendering label PDFs. */
export interface LabelCalibration {
  dx_mm: DecimalString | number;
  dy_mm: DecimalString | number;
  sx: DecimalString | number;
  sy: DecimalString | number;
}

export interface BalanceSettings {
  /** Relative tolerance as a fraction: "0.01" is 1 %. */
  tolerance_rel: DecimalString;
  tolerance_abs_g: DecimalString;
  auto_advance_ms: number;
}

/** Workspace.settings, merged over the backend defaults (backend/common/models.py). */
export interface WorkspaceSettings {
  theme?: string;
  label_calibration?: Record<string, LabelCalibration>;
  balance?: BalanceSettings;
  /** Schedule switches keyed by enabled_key (backend/common/schedules.py); missing means on. */
  schedules?: Record<string, boolean>;
  assistant?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface Workspace {
  id: Uuid;
  name: string;
  ship_to: ShipTo;
  /** ISO 3166-1 alpha-2 market for supplier prices, e.g. "CA". */
  market_country: string;
  /** ISO 4217, e.g. "CAD". */
  currency: string;
  /** Compliance market code, e.g. "CA" or "EU". */
  compliance_market: string;
  settings: WorkspaceSettings;
  role: WorkspaceRole | null;
}

export interface Session {
  user: User;
  workspace: Workspace | null;
  role: WorkspaceRole | null;
  settings: WorkspaceSettings;
}

export const sessionQueryKey = ["session"] as const;

export const sessionQuery = queryOptions({
  queryKey: sessionQueryKey,
  queryFn: ({ signal }) => apiGet<Session>("/auth/me", { signal }),
  staleTime: 5 * 60_000,
  retry: false,
});

/**
 * Reads the cached session without fetching it: RequireSession loads it once for the whole
 * authenticated app, and these observers re-render when settings change it.
 */
function useCachedSession(): Session | undefined {
  return useQuery({ ...sessionQuery, enabled: false }).data;
}

/** The current session. Only call under the authenticated layout (RequireSession). */
export function useSession(): Session {
  const session = useCachedSession();
  if (!session) throw new Error("useSession() was called outside the authenticated layout");
  return session;
}

export function useWorkspace(): Workspace | null {
  return useCachedSession()?.workspace ?? null;
}

/** The workspace currency for prices (George's default is CAD). */
export function useWorkspaceCurrency(): string {
  return useWorkspace()?.currency ?? "CAD";
}

/** Owners and editors may change data; viewers get read-only screens. */
export function useCanEdit(): boolean {
  const role = useCachedSession()?.role;
  return role === "owner" || role === "editor";
}

export function displayName(user: User): string {
  const fullName = `${user.first_name} ${user.last_name}`.trim();
  return fullName || user.username;
}

export async function login(queryClient: QueryClient, username: string, password: string): Promise<Session> {
  const session = await apiPost<Session>("/auth/login", { username, password });
  queryClient.setQueryData(sessionQueryKey, session);
  return session;
}

export async function logout(queryClient: QueryClient): Promise<void> {
  try {
    await apiPost("/auth/logout");
  } finally {
    queryClient.clear();
  }
}
